






















































import type { PropOptions } from 'vue';
import Vue from 'vue';
// @ts-ignore
import { VueMaskDirective } from 'v-mask';
import { onFontsReady } from '@/components/utils/browser/core';
import BravadoText from '~/components/common/BravadoText/BravadoText.vue';

interface IData {
  dynamicMask: string;
  isFocused: boolean;
}

export default Vue.extend({
  name: 'BravadoTextInputV3',
  components: { BravadoText },
  directives: {
    mask: VueMaskDirective,
  },
  props: {
    value: {
      type: [String, Number],
      default: '',
      required: false,
    } as PropOptions<string | number>,
    valueFormatter: {
      type: Function,
      default: null,
    } as PropOptions<(<T>(val: T) => T) | null>,
    valueParser: {
      type: Function,
      default: null,
    } as PropOptions<(<T>(val: T) => T) | null>,
    isValid: {
      type: Boolean,
      default: true,
    } as PropOptions<boolean>,
    label: {
      type: String,
      default: '',
    } as PropOptions<string>,
    hint: {
      type: String,
      default: '',
    } as PropOptions<string>,
    multiline: {
      type: Boolean,
      default: false,
    } as PropOptions<boolean>,
    inputStyle: {
      type: Object,
      default: () => ({}),
    } as PropOptions<Record<string, string>>,
    maxlength: {
      type: Number,
      default: null,
    } as PropOptions<number>,
    mandatory: {
      type: Boolean,
      default: false,
    } as PropOptions<boolean>,
    variant: {
      type: String,
      default: 'default',
      validator: value => ['default', 'borderless'].includes(value),
    } as PropOptions<string>,
    mask: {
      type: String,
      default: '',
    },
    autofocus: {
      type: Boolean,
      default: false,
    } as PropOptions<boolean>,
    maskFormatter: {
      type: Function,
      default: null,
    } as PropOptions<(<T>(value: string, initialMask: string, currentMask: string) => T) | null>,
    size: {
      type: String,
      default: 'md',
      validator: value => ['sm', 'md'].includes(value),
    } as PropOptions<'sm' | 'md'>,
    readonlyText: {
      type: String,
      default: '',
    } as PropOptions<string>,
    type: {
      type: String,
      default: 'text',
    } as PropOptions<'text' | 'number' | 'search' | 'email'>,
    /**
     * Slot/icon has interactivity on it, otherwise - focus on input on click
     */
    interactiveSlot: {
      type: Boolean,
      default: true,
    } as PropOptions<boolean>,
  },
  data(): IData {
    return {
      dynamicMask: this.mask,
      isFocused: false,
    };
  },
  computed: {
    inputProps(): Record<string, unknown> {
      const value = this.valueFormatter && this.value ? this.valueFormatter(this.value) : this.value;

      return {
        ref: 'input',
        autocomplete: 'off',
        class: 'bravado-text-input-v3__field',
        style: this.inputStyle,
        maxlength: this.maxlength,
        value,
        ...this.$attrs,
      };
    },
    inputListeners(): Record<string, Function> {
      return {
        ...this.$listeners,
        input: this.handleInput,
        focus: this.handleFocus,
        blur: this.handleBlur,
      };
    },
    rootClass(): Record<string, boolean> {
      return {
        'bravado-text-input-v3': true,
        'bravado-text-input-v3--icon': Boolean(this.$slots.icon),
        'bravado-text-input-v3--append': Boolean(this.$slots.append),
        'bravado-text-input-v3--invalid': !this.isValid,
        'bravado-text-input-v3--interactive-slot': this.interactiveSlot,
        [`bravado-text-input-v3--${this.variant}`]: true,
        [`bravado-text-input-v3--${this.size}`]: Boolean(this.size),
      };
    },
    containerClass(): Record<string, boolean> {
      return {
        'bravado-text-input-v3__container': true,
      };
    },
  },
  mounted(): void {
    this.focusOnMounted();

    onFontsReady(this.setInputPadding);
  },
  methods: {
    handleIconClick(): void {
      this.$emit('handleIconClick');
      if (this.$refs.input) (this.$refs.input as HTMLElement).focus();
    },
    blur(): void {
      (this.$refs.input as HTMLTextAreaElement).blur();
    },
    focus(): void {
      (this.$refs.input as HTMLTextAreaElement).focus();
    },
    focusOnMounted(): void {
      if (!this.autofocus) return;

      this.$nextTick(() => {
        const input = this.$el.querySelector('input');
        const textarea = this.$el.querySelector('textarea');

        if (input) input.focus();
        else if (textarea) textarea.focus();
      });
    },
    handleInput(event: Event): void {
      const target = event.target as HTMLInputElement | HTMLTextAreaElement;
      const value = this.valueParser ? this.valueParser(target.value) : target.value;

      if (this.maskFormatter) {
        this.dynamicMask = this.maskFormatter(target.value, this.mask, this.dynamicMask);
      }

      if (target.nodeName === 'TEXTAREA') {
        target.style.height = '76px';
        target.style.height = target.scrollHeight + 12 + 'px';
      }

      this.$emit('input', value);
    },
    handleFocus(event: Event): void {
      this.isFocused = true;
      this.$emit('focus', event);
    },
    handleBlur(event: Event): void {
      this.isFocused = false;
      this.$emit('blur', event);
    },
    setInputPadding() {
      if (!this.$refs.readOnlyText) return;

      const INPUT_LEFT_PADDING = 16;

      const { width } = (this.$refs.readOnlyText as HTMLElement).getBoundingClientRect();

      (this.$refs.input as HTMLElement).style.paddingLeft = `${width + INPUT_LEFT_PADDING}px`;
    },
  },
});
